import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUnifiedAuth } from '../context/UnifiedAuthContext';
import { ThemeToggle } from '../context/ThemeContext';
import { AlertBanner, Card, Button } from '../components/ui';
import { AuthModal } from '../components/auth';
import { USER_ROLES } from '../config/userRoles';

// Startbildschirm
function Welcome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, userProfile } = useUnifiedAuth();
  
  // Quiz join state
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState('');
  
  // Auth modal state - open if auth=true in URL
  const [showLoginModal, setShowLoginModal] = useState(searchParams.get('auth') === 'true');
  
  // Pre-fill joinCode from URL if provided
  const joinCodeFromUrl = searchParams.get('joinCode');
  
  useEffect(() => {
    if (joinCodeFromUrl && !sessionId) {
      setSessionId(joinCodeFromUrl);
    }
  }, [joinCodeFromUrl]);

  // Redirect authenticated users based on role
  useEffect(() => {
    // Don't redirect if user wants to join a quiz (has sessionId or is on welcome to enter code)
    if (isAuthenticated && user && userProfile && !sessionId && !searchParams.get('quiz')) {
      // Role-based redirect
      if (userProfile.role === USER_ROLES.STUDENT) {
        navigate('/student/dashboard' + (joinCodeFromUrl ? `?joinCode=${joinCodeFromUrl}` : ''));
      } else if (userProfile.role === USER_ROLES.ADMIN) {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard'); // Teacher
      }
    }
  }, [isAuthenticated, user, userProfile, navigate, joinCodeFromUrl, sessionId, searchParams]);


  // Join quiz with the new modular view
  const joinQuiz = () => {
    if (!sessionId.trim()) {
      setError("Bitte gib einen Quiz-Code ein");
      return;
    }
    setError('');
    navigate(`/play/${sessionId.trim()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 flex flex-col theme-transition">
      {/* Theme Toggle */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      
      <div className="max-w-2xl mx-auto flex-grow">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-3 text-gray-800 dark:text-white">
            Quiz
          </h1>
          <p className="text-gray-600 dark:text-gray-300 text-lg">Willkommen! Teste dein Wissen mit interaktiven Quizzes.</p>
        </div>
        
        {error && (
          <AlertBanner
            variant="danger"
            message={error}
            onDismiss={() => setError('')}
            className="mb-6"
          />
        )}
        
        {/* Main Content - Join Quiz */}
        <Card className="p-8 mb-8 border-2 border-success-200 dark:border-success-800">
          <div className="flex items-center mb-4">
            <span className="text-4xl mr-3"></span>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Quiz beitreten</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-6">Gib den Quiz-Code ein, um an einem Quiz teilzunehmen</p>
          
          <div className="space-y-4">
            <input 
              type="text" 
              placeholder="Quiz-Code eingeben..."
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  joinQuiz();
                }
              }}
              className="w-full p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-success-500 focus:outline-none text-xl font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center tracking-widest"
              autoFocus
            />
            <Button
              variant="success"
              size="lg"
              fullWidth
              onClick={joinQuiz}
              disabled={!sessionId}
            >
              Quiz starten →
            </Button>
          </div>
        </Card>
      </div>
      
      {/* Footer with login link */}
      <footer className="mt-auto pt-8 pb-4">
        <div className="max-w-2xl mx-auto text-center space-y-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Quiz System</span>
          <div>
            <button
              onClick={() => setShowLoginModal(true)}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 underline underline-offset-2 theme-transition"
            >
              Anmelden / Registrieren
            </button>
          </div>
        </div>
      </footer>
      
      {/* Unified Auth Modal */}
      <AuthModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        redirectAfterLogin="/"
        initialInviteCode={joinCodeFromUrl || ''}
      />
    </div>
  );
}

export default Welcome;
